
import { authenticated } from '@eneco/dc/services';
import {
  DC_Repositories_Base_Enumerations_Label,
  DC_Repositories_Base_Enumerations_BusinessUnit,
} from '@monorepo-types/dc';
import { NextAuthOptions } from 'next-auth';
import OktaProvider from 'next-auth/providers/okta';

/* eslint-disable require-await */
export const getAuthOptions = ({
  businessUnit,
  label,
}: {
  businessUnit?: DC_Repositories_Base_Enumerations_BusinessUnit;
  label?: DC_Repositories_Base_Enumerations_Label;
} = {}): NextAuthOptions => ({
  providers: [
    OktaProvider({
      clientId: process.env.OKTA_OAUTH2_CLIENT_ID as string,
      clientSecret: process.env.OKTA_OAUTH2_CLIENT_SECRET as string,
      issuer: process.env.OKTA_OAUTH2_ISSUER as string,
      idToken: true,
      authorization: {
        url: process.env.OKTA_OAUTH2_AUTHORIZATION_URL as string,
        params: {
          scope: 'openid offline_access email profile',
          redirect_uri: `${process.env.NEXTAUTH_URL}/callback/okta`,
        },
      },
      checks: ['pkce', 'state', 'nonce'],
    }),
  ],
  pages: {
    signIn: '/login',
  },
  callbacks: {
    async jwt({ token, account, profile }) {
      if (account) {
        token.access_token = account.access_token;
        token.id_token = account.id_token;
        token.providerAccountId = account.providerAccountId;
        token.accessTokenExpires = account.expires_at;
      }

      if (profile) {
        const customerId = (profile as { customerId: number }).customerId;
        token.customerId = `${customerId}`;

        if (businessUnit && label) {
          authenticated({ businessUnit, customerId, label }, { token: token.access_token });
        }
      }

      if (Date.now() >= (token.accessTokenExpires as number) * 1000) {
        token.error = 'RefreshAccessTokenError';
      }

      return token;
    },
    async session({ session, token }) {
      session.accessToken = token.access_token;
      session.idToken = token.id_token;
      session.error = token.error;

      if (session.user) {
        session.providerAccountId = token.providerAccountId;
        session.user.customerId = token.customerId;
        session.user.image = null;
      }

      return session;
    },
    async redirect({ url, baseUrl }) {
      // Rewrite account activation success URL to show a dedicated landing page
      function rewriteAccountActivationSuccessUrl(urlObj: URL): string | null {
        // Remove the 'eneco' check when 'oxxio' supports this landing page as well. 'woonenergie' can be ignored as it has another flow.
        if (label === 'eneco' && urlObj.pathname === `/mijn-${label}/` && urlObj.searchParams.get('type_hint')?.toLowerCase() === 'activation') {
          return `${baseUrl}/mijn-${label}/account-geactiveerd`;
        }
        return null;
      }

      try {
        // Support relative and complete callback URLs
        const urlObj = url.startsWith('/') ? new URL(url, baseUrl) : new URL(url);

        // Only allow callback URLs on the same origin
        if (urlObj.origin === baseUrl) {
          const rewrittenUrl = rewriteAccountActivationSuccessUrl(urlObj);
          if (rewrittenUrl) {
            return rewrittenUrl;
          }

          return urlObj.toString();
        }
      } catch {
        // Invalid URL format, fallback to baseUrl
        return baseUrl;
      }

      return baseUrl;
    }
  },
  secret: process.env.NEXTAUTH_SECRET as string,
});
/* eslint-enable require-await */

export default getAuthOptions;
